import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';
import { SubscriptionsApi } from '../../api/subscriptions.api';
import { Voucher } from '../../models/voucher.model';
import { SubscriptionsApiActions } from '../subscriptions/subscriptions.actions';
import { VouchersApiActions } from './vouchers.actions';
import { ToasterService } from 'app/services/toaster.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class VouchersEffect {
  private api: SubscriptionsApi = inject(SubscriptionsApi);
  private toasterService: ToasterService = inject(ToasterService);
  private router: Router = inject(Router);
  private actions$: Actions = inject(Actions);
  private translate: TranslateService = inject(TranslateService);

  getVouchersHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VouchersApiActions.fetchVouchers),
      exhaustMap(() => {
        return this.api.getVouchersHistory$();
      }),
      map((vouchers: Voucher[]) => {
        return VouchersApiActions.retrievedVouchers({ vouchers });
      })
    );
  });

  getVoucherPdf$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VouchersApiActions.fetchVoucherPDF),
      exhaustMap((action) => {
        return this.api.getVoucherPdf$(action.voucherId).pipe(
          map((response: Blob) => {
            this.toasterService.success(
              '',
              this.translate.instant('dashboard.subscription.voucher.mailSent')
            );
            return VouchersApiActions.retrievedVoucherPDF({
              voucherId: action.voucherId,
            });
          })
        );
      })
    );
  });

  addVoucher$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VouchersApiActions.postVoucher),
      exhaustMap((action) => {
        return this.api.useVoucher$(action.code).pipe(
          map(() => {
            return VouchersApiActions.succeededToAddVoucher();
          }),
          catchError(() => {
            return of(VouchersApiActions.failedToAddVoucher());
          })
        );
      })
    );
  });

  handleAddVoucherSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VouchersApiActions.succeededToAddVoucher),
      map(() => {
        this.router.navigate(['dashboard', 'subscriptions']);
        this.toasterService.success(
          null,
          this.translate.instant(
            `dashboard.subscription.voucher.addVoucherMessages.success`
          )
        );
        return SubscriptionsApiActions.fetchActiveSubscription();
      })
    );
  });

  handleAddVoucherError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(VouchersApiActions.failedToAddVoucher),
        tap(() => {
          this.toasterService.error(
            null,
            this.translate.instant(
              `dashboard.subscription.voucher.addVoucherMessages.error`
            )
          );
        })
      );
    },
    { dispatch: false }
  );
}
